// LegacyRedirects.jsx - Handles redirects from old URLs to new ones
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Static redirect mappings from old URLs to new URLs
const STATIC_REDIRECTS = {
  // Common old URL patterns
  '/index.html': '/',
  '/index.php': '/',
  '/home': '/',
  '/accueil': '/',

  // Old Normandie blog posts -> new Vendée equivalents (preserve link equity)
  '/blog/quand-tondre-pelouse-normandie': '/blog/quand-tondre-pelouse-vendee',
  '/blog/taille-haies-periode-ideale': '/blog/quand-tailler-haies-vendee',
  '/blog/jardinier-domicile-caen-comment-choisir': '/blog/choisir-jardinier-domicile-vendee',
  '/blog/prix-tonte-pelouse-calvados-2025': '/tarifs',
  
  // Old service pages
  '/jardinage': '/services',
  '/nos-services': '/services',
  '/prestations': '/services',
  '/service': '/services',
  
  // Old contact patterns
  '/contact.html': '/contact',
  '/contact.php': '/contact',
  '/contactez-nous': '/contact',
  '/nous-contacter': '/contact',
  
  // Old quote patterns
  '/devis-gratuit': '/devis',
  '/demande-devis': '/devis',
  '/demander-un-devis': '/devis',
  '/quote': '/devis',
  
  // Old pricing patterns
  '/prix': '/tarifs',
  '/pricing': '/tarifs',
  '/nos-tarifs': '/tarifs',
  '/tarif': '/tarifs',
  
  // Old zone patterns
  '/zones': '/zone',
  '/secteur': '/zone',
  '/intervention': '/zone',
  '/zone-intervention': '/zone',
  
  // Old FAQ patterns
  '/faq.html': '/faq',
  '/questions': '/faq',
  '/aide': '/faq',
  
  // Old legal pages
  '/mentions-legales.html': '/mentions-legales',
  '/legal': '/mentions-legales',
  '/cgv': '/mentions-legales',
  '/cgu': '/mentions-legales',
  '/privacy': '/politique-confidentialite',
  '/confidentialite': '/politique-confidentialite',
  
  // Service-specific old patterns
  '/tonte-pelouse': '/services/tonte',
  '/tonte-gazon': '/services/tonte',
  '/entretien-pelouse': '/services/tonte',
  '/taille-haie': '/services/haies',
  '/taille-haies': '/services/haies',
  '/debroussaillage': '/services/debroussaillage',
  '/desherbage': '/services/desherbage',
  '/bricolage': '/services/bricolage',
  '/surveillance-maison': '/services/vigilance',
  '/gardiennage': '/services/vigilance',
  
  // Old booking/reservation patterns
  '/reserver': '/devis',
  '/reservation': '/devis',
  '/book': '/devis',
  '/booking': '/devis',

  // Old English (/en/) slugs -> new English structure (preserve link equity)
  '/en/holiday-home-garden-maintenance-vendee': '/en/holiday-home-garden-care-vendee',
  '/en/english-speaking-gardener-vendee': '/en/',
};

// Town name normalizations
const TOWN_NORMALIZATIONS = {
  'st-gilles': 'saint-gilles-croix-de-vie',
  'saint-gilles': 'saint-gilles-croix-de-vie',
  'sgcv': 'saint-gilles-croix-de-vie',
  'st-hilaire': 'saint-hilaire-de-riez',
  'la-roche': 'la-roche-sur-yon',
  'laroche': 'la-roche-sur-yon',
  'bretignolles': 'bretignolles-sur-mer',
  'aiguillon': 'laiguillon-sur-vie',
  'l-aiguillon': 'laiguillon-sur-vie',
  'chapelle-hermier': 'la-chapelle-hermier',
  'chaize-giraud': 'la-chaize-giraud',
  'mothe-achard': 'la-mothe-achard',
};

// Pattern-based redirects
const PATTERN_REDIRECTS = [
  // CRITICAL: /zones/* -> /zone/* (canonical URL structure)
  { pattern: /^\/zones\/(.+)$/, replacement: '/zone/$1' },
  { pattern: /^\/zones\/?$/, replacement: '/zone' },
  
  // Old town URLs: /ville/coex -> /zone/coex
  { pattern: /^\/ville\/(.+)$/, replacement: '/zone/$1' },
  { pattern: /^\/commune\/(.+)$/, replacement: '/zone/$1' },
  { pattern: /^\/location\/(.+)$/, replacement: '/zone/$1' },
  
  // Old service+town combos: /tonte-coex -> /services/tonte/coex
  // IMPORTANT: Exclude specific problem-solution pages from these patterns
  { pattern: /^\/tonte-(.+)$/, replacement: '/services/tonte/$1' },
  // Exclude /haies-trop-hautes from redirect (it's a dedicated problem-solution page)
  { pattern: /^\/haies-(?!trop-hautes)(.+)$/, replacement: '/services/haies/$1' },
  { pattern: /^\/debroussaillage-(.+)$/, replacement: '/services/debroussaillage/$1' },
  { pattern: /^\/desherbage-(.+)$/, replacement: '/services/desherbage/$1' },
  { pattern: /^\/vigilance-(.+)$/, replacement: '/services/vigilance/$1' },
  { pattern: /^\/bricolage-(.+)$/, replacement: '/services/bricolage/$1' },
  
  // Old service pages: /service/tonte -> /services/tonte
  { pattern: /^\/service\/(.+)$/, replacement: '/services/$1' },
  
  // Remove .html/.php extensions
  { pattern: /^\/(.+)\.html$/, replacement: '/$1' },
  { pattern: /^\/(.+)\.php$/, replacement: '/$1' },
  
  // Old jardinier patterns
  { pattern: /^\/jardinier-(?!de-confiance-vendee)(.+)$/, replacement: '/zone/$1' },
  { pattern: /^\/jardinage-(?!senior-vendee$)(.+)$/, replacement: '/zone/$1' },
];

function normalizeTownSlug(town) {
  const townLower = town.toLowerCase().trim();
  return TOWN_NORMALIZATIONS[townLower] || townLower;
}

function getRedirectPath(pathname) {
  // Normalize path
  let path = pathname.toLowerCase().replace(/\/$/, '') || '/';
  
  // Check static redirects first
  if (STATIC_REDIRECTS[path]) {
    return STATIC_REDIRECTS[path];
  }
  
  // Check pattern-based redirects
  for (const { pattern, replacement } of PATTERN_REDIRECTS) {
    if (pattern.test(path)) {
      let newPath = path.replace(pattern, replacement);
      
      // Normalize town slug if present
      if (newPath.includes('/zone/') || newPath.includes('/services/')) {
        const parts = newPath.split('/');
        if (parts.length >= 3) {
          parts[parts.length - 1] = normalizeTownSlug(parts[parts.length - 1]);
          newPath = parts.join('/');
        }
      }
      
      return newPath;
    }
  }
  
  return null;
}

// Log 404 to backend for analysis
async function log404ToBackend(path) {
  try {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/log-404`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        path, 
        referrer: document.referrer,
        timestamp: new Date().toISOString()
      }),
    });
  } catch (e) {
    // Silently fail - don't break the app for logging
  }
}

export function useLegacyRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const redirectPath = getRedirectPath(location.pathname);
    
    if (redirectPath && redirectPath !== location.pathname) {
      // Perform redirect
      navigate(redirectPath, { replace: true });
    }
  }, [location.pathname, navigate]);
}

// Component version for use in routes
export default function LegacyRedirects({ children }) {
  useLegacyRedirect();
  return children;
}

// Export for testing
export { getRedirectPath, STATIC_REDIRECTS };
